/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Monster from "model/monster/Monster"

export const computeSplitPoint = (monster: Monster) => {
    const countLinesInString = (str: string | {name: string, desc: string}): number => {
        if (typeof str !== 'string') {
            return countLinesInString(str.name + ". " + str.desc);
        }
        if (str.includes('\n')) {
            const paragraphs = str.split('\n');
            let lines = 0;
            paragraphs.forEach(paragraph => {
                lines += countLinesInString(paragraph);
            });
            return lines;
        }
        // assume 66 characters per line, unless the lines are indented
        let charsPerLine = 66;
        if (str.startsWith('>')) charsPerLine -= 10;
        if (str.startsWith('>>')) charsPerLine -= 10;
        const lines = Math.floor(str.length / charsPerLine) + 1;
        return lines;
    }

    // Deteremine the best split point:
    const getSectionParagraphs = (array: (Ability | string) [], preText?: string, postText?: string): (Ability | string)[] => {
        if (!array || array.length == 0) {
            array = [];
        }
        const results: (Ability | string)[] = [...array];
        if (preText) {
            results.unshift(preText);
        }
        if (postText) {
            results.push(postText);
        }
        if (results.length) {
            const newRow = 'space\nheader\nheader\nline\n';
            if (typeof results[0] === 'string') {
                results[0] = newRow + results[0];
            } else {
                results[0] = newRow + results[0].name + ':' + results[0].desc;
            }
        }
        return results;
    }
    const paragraphs = [monster.desc,
                        ...(monster.traits ? ([...monster.traits]) : []),
                        ...getSectionParagraphs(monster.actions      ),
                        ...getSectionParagraphs(monster.spells       ),
                        ...getSectionParagraphs(monster.bonus_actions),
                        ...getSectionParagraphs(monster.reactions    ),
                        ...getSectionParagraphs(monster.inventory    ),
                        ...getSectionParagraphs(monster.legendary_actions, monster.legendary_desc),
                        ...getSectionParagraphs(monster.mythic_actions,    monster.mythic_desc   ),
                        ...getSectionParagraphs(monster.regional_actions,  monster.regional_desc, monster.regional_desc_end),
                        ...getSectionParagraphs(monster.lair_actions,      monster.lair_desc,     monster.lair_desc_end    )
                    ].filter(o => o);
    const linesOnLeftBySplitPoint = [];
    linesOnLeftBySplitPoint[0] = 17;
    if (monster.skillSet.skills.size) linesOnLeftBySplitPoint[0]++;
    if (monster.damage_vulnerabilities) linesOnLeftBySplitPoint[0]++;
    if (monster.damage_immunities) linesOnLeftBySplitPoint[0]++;
    if (monster.condition_immunities) linesOnLeftBySplitPoint[0]++;
    if (monster.languages) linesOnLeftBySplitPoint[0]++;

    for (let split = 1; split <= paragraphs.length; split++) {
        linesOnLeftBySplitPoint[split] = linesOnLeftBySplitPoint[split - 1] + countLinesInString(paragraphs[split - 1]);
    }
    const totalLines = linesOnLeftBySplitPoint[paragraphs.length];
    const halfLines = Math.floor(totalLines / 2);
    monster.splitPoint = 0;
    for (let split = 1; split < paragraphs.length; split++) {
        const deltaCurrent = Math.abs(linesOnLeftBySplitPoint[monster.splitPoint] - halfLines);
        const deltaNext = Math.abs(linesOnLeftBySplitPoint[split] - halfLines);
        if (deltaNext < deltaCurrent) {
            monster.splitPoint = split;
        } else {
            break;
        }
    }
}
